'use client';

import { useAnalyticsSkuViewedContext } from '@ria-client/segment/Analytics/AnalyticsSkuViewed';
import type { FinancialAdvisorsSummaryTableEntityType } from '@ria-client/supergraph/structuredContent/hydration/FinancialAdvisorsSummaryTable/types';
import { RatingSummary } from '@ria-client/tw-shared-ui';
import { NW_RATING_DEFAULT_TOOLTIP } from '../lib/financial-advisors/constants';

export interface TableCellCompanyProps {
  marketplaceEntity: FinancialAdvisorsSummaryTableEntityType;
}

export function TableCellCompany({ marketplaceEntity }: TableCellCompanyProps) {
  const { fireShoppingFinishedEvent } = useAnalyticsSkuViewedContext();
  const link =
    marketplaceEntity.isMonetizing && marketplaceEntity.applyLink
      ? marketplaceEntity.applyLink
      : marketplaceEntity.reviewLink;
  const handleClick = (destinationUrl: string) => () => {
    fireShoppingFinishedEvent({
      ctaType: 'product_image',
      destinationUrl,
      finishType: 'internal_content',
      entryPointPageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  };
  const image = (
    <img
      alt={`${marketplaceEntity.displayName} logo`}
      className="mx-auto aspect-[208/96] w-full max-w-[175px] object-contain"
      src={marketplaceEntity.image.source}
    />
  );

  return (
    <td className="sticky left-[-1px] z-[1] w-[190px] min-w-[190px] bg-white p-0 align-top md:relative lg:w-[200px] lg:border-b lg:border-border lg:pb-7">
      <div className="flex max-w-[150px] flex-col items-start bg-white pt-2 pb-11 sm:max-w-[200px] sm:pt-[5px] sm:pb-2">
        {link ? (
          <a
            className="mx-auto"
            href={link}
            rel="nofollow noopener"
            target="_blank"
            onClick={handleClick(link)}
          >
            {image}
          </a>
        ) : (
          image
        )}
        <span className="typography-text-small mt-2">{marketplaceEntity.displayName}</span>
        <div className="hidden w-full pt-2 lg:block">
          <RatingSummary
            reviewLink={marketplaceEntity.reviewLink}
            starRating={marketplaceEntity.starRating}
            starRatingTooltip={marketplaceEntity.starRatingTooltip ?? NW_RATING_DEFAULT_TOOLTIP}
          />
          <RiaDates marketplaceEntity={marketplaceEntity} />
        </div>
      </div>
    </td>
  );
}

function RiaDates({
  marketplaceEntity,
}: {
  marketplaceEntity: FinancialAdvisorsSummaryTableEntityType;
}) {
  if (
    !marketplaceEntity.isRiaOffer ||
    !marketplaceEntity.reviewedAtDate ||
    !marketplaceEntity.periodConsideredDate
  ) {
    return null;
  }

  return (
    <div className="typography-text-small mt-2 flex flex-col text-muted-foreground">
      <span>{marketplaceEntity.reviewedAtDate}</span>
      <span>{marketplaceEntity.periodConsideredDate}</span>
    </div>
  );
}
